// handle question/answer branching
const getNextQuestionId = (currentQuestion, answer) => {
    let nextQuestionId = null;

    switch (currentQuestion.input.type) {
        case 'boolean':
            if (
                currentQuestion.input.nextQuestion &&
                currentQuestion.input.nextQuestion.length > 1
            ) {
                if (answer) {
                    nextQuestionId = currentQuestion.input.nextQuestion[0];
                } else {
                    nextQuestionId = currentQuestion.input.nextQuestion[1];
                }
            } else if (currentQuestion.input.nextQuestion) {
                nextQuestionId = currentQuestion.input.nextQuestion[0];
            }
            break;

        case 'textarea':
        case 'checkboxes':
        case 'number':
        case 'text':
            nextQuestionId = currentQuestion.input.nextQuestion
                ? currentQuestion.input.nextQuestion[0]
                : null;
            break;

        case 'select':
            if (
                currentQuestion.input.nextQuestion &&
                currentQuestion.input.nextQuestion.length > 1
            ) {
                // find which selection was made
                nextQuestionId =
                    currentQuestion.input.nextQuestion[currentQuestion.input.items.indexOf(answer)];
            } else if (currentQuestion.input.nextQuestion) {
                nextQuestionId = currentQuestion.input.nextQuestion[0];
            }
            break;
        default:
            break;
    }

    return nextQuestionId;
};

// from a json-forms formData submission, extract the answer given
const extractQuestionAnswer = (question, formData) => {
    // input validation
    if (!question.input.type) {
        throw new Error("Expecting question 'input' property to have a 'type' property");
    }

    switch (question.input.type) {
        case 'number':
        case 'textarea':
        case 'text':
        case 'boolean':
            return formData.formControl;
        case 'select':
        case 'checkboxes':
            return formData;
        default:
            return null;
    }
};

// from a json-forms question, attach the correct formData preloaded answer
const createFormData = (question, answer) => {
    // input validation
    if (!question.input.type) {
        throw new Error("Expecting question 'input' property to have a 'type' property");
    }

    switch (question.input.type) {
        case 'number':
        case 'boolean':
        case 'textarea':
        case 'text':
            return { formControl: answer };
        case 'select':
        case 'checkboxes':
            return answer;
        default:
            return null;
    }
};

// create a json forms question schema for a given questionnaire question definition
const generateQuestionSchema = question => {
    // question validation
    if (!question.input) {
        throw new Error("Expecting question to have an 'input' property");
    }

    if (!question.id) {
        throw new Error("Expecting question to have an 'id' property");
    }

    if (!question.section) {
        throw new Error("Expecting question to have a 'section' property");
    }

    if (!question.title) {
        throw new Error("Expecting question to have a 'title' property");
    }

    // input validation
    if (!question.input.type) {
        throw new Error("Expecting question 'input' property to have a 'type' property");
    }

    switch (question.input.type) {
        case 'boolean':
            return {
                id: question.id,
                section: question.section,
                subsection: question.subsection,
                documentType: question.documentType,
                nextQuestion: question.input.nextQuestion ? question.input.nextQuestion : [],
                type: 'boolean',
                schema: {
                    title: question.title,
                    type: 'object',
                    required: ['formControl'],
                    properties: {
                        formControl: {
                            type: 'boolean',
                            title: question.description,
                            default: question.input.default ? question.input.default : false,
                        },
                    },
                },
                uiSchema: {
                    formControl: {
                        'ui:widget': 'radio',
                        'ui:options': {
                            inline: !!question.input.inline,
                        },
                    },
                },
            };
        case 'text':
            return {
                id: question.id,
                section: question.section,
                subsection: question.subsection,
                documentType: question.documentType,
                nextQuestion: question.input.nextQuestion ? question.input.nextQuestion : null,
                type: 'text',
                schema: {
                    title: question.title,
                    type: 'object',
                    required: question.input.notRequired ? [] : ['formControl'],
                    properties: {
                        formControl: {
                            type: 'string',
                            title: question.description,
                            default: question.input.default ? question.input.default : '',
                        },
                    },
                },
            };
        case 'select':
            return {
                id: question.id,
                section: question.section,
                subsection: question.subsection,
                documentType: question.documentType,
                nextQuestion: question.input.nextQuestion ? question.input.nextQuestion : [],
                type: 'select',
                title: question.title,
                schema: question.input.selectOne
                    ? {
                          title: question.description,
                          type: 'string',
                          enum: question.input.items.map(item => {
                              return item;
                          }),
                          default: question.input.default ? question.input.default : '',
                      }
                    : {
                          title: question.description,
                          type: 'array',
                          uniqueItems: true,
                          items: {
                              type: 'string',
                              enum: question.input.items.map(item => {
                                  return item;
                              }),
                          },
                      },
            };
        case 'checkboxes': {
            return {
                id: question.id,
                section: question.section,
                subsection: question.subsection,
                documentType: question.documentType,
                nextQuestion: question.input.nextQuestion ? question.input.nextQuestion : null,
                type: 'checkboxes',
                schema: {
                    title: question.title, //title: question.description,
                    type: 'array',
                    items: {
                        type: 'string',
                        enum: question.input.items.map(item => {
                            return item;
                        }),
                    },
                    uniqueItems: true,
                },
                uiSchema: {
                    'ui:widget': 'checkboxes',
                    'ui:options': {
                        inline: !!question.input.inline,
                    },
                },
            };
        }
        case 'number':
            return {
                id: question.id,
                section: question.section,
                subsection: question.subsection,
                documentType: question.documentType,
                nextQuestion: question.input.nextQuestion ? question.input.nextQuestion : null,
                type: 'number',
                schema: {
                    title: question.title,
                    type: 'object',
                    required: ['formControl'],
                    properties: {
                        formControl: {
                            type: 'number',
                            title: question.description,
                            default: question.input.default ? question.input.default : null,
                        },
                    },
                },
            };

        case 'textarea':
            return {
                id: question.id,
                section: question.section,
                subsection: question.subsection,
                documentType: question.documentType,
                nextQuestion: question.input.nextQuestion ? question.input.nextQuestion : null,
                type: 'textarea',
                schema: {
                    title: question.title,
                    type: 'object',
                    required: question.input.notRequired ? [] : ['formControl'],
                    properties: {
                        formControl: {
                            type: 'string',
                            title: question.description,
                            default: question.input.default ? question.input.default : '',
                        },
                    },
                },
                uiSchema: {
                    formControl: {
                        'ui:widget': 'textarea',
                        'ui:options': {
                            rows: question.input.rows ? question.input.rows : 3,
                        },
                    },
                },
            };

        default:
            break;
    }
};

const createMermaidDefinition = async questions => {
    let mermaidDefinition = 'graph TD\n';

    if (questions && questions instanceof Array) {
        let clicks = [];

        let questionList = [];
        questions.forEach(question => {
            const nextQuestion = question.input.nextQuestion;

            questionList.push(question.id);

            if (
                nextQuestion &&
                nextQuestion.length &&
                nextQuestion[nextQuestion.length - 1] !== null
            ) {
                for (let i = 0; i < nextQuestion.length; i++) {
                    switch (question.input.type) {
                        case 'boolean':
                            if (nextQuestion.length === 1) {
                                mermaidDefinition += `${question.id}(${question.title}) --> ${nextQuestion[i]}(${nextQuestion[i]});\n`;
                            } else {
                                mermaidDefinition += `${question.id}(${question.title}) -->|${
                                    i === 0 ? 'yes' : 'no'
                                }| ${nextQuestion[i]}(${nextQuestion[i]});\n`;
                            }
                            break;
                        case 'select':
                            if (nextQuestion.length === 1) {
                                mermaidDefinition += `${question.id}(${question.title}) --> ${nextQuestion[i]}(${nextQuestion[i]});\n`;
                            } else {
                                mermaidDefinition += `${question.id}(${question.title}) -->|${question.input.items[i]}| ${nextQuestion[i]}(${nextQuestion[i]});\n`;
                            }
                            break;
                        default:
                            mermaidDefinition += `${question.id}(${question.title}) --> ${nextQuestion[i]}(${nextQuestion[i]});\n`;
                            break;
                    }
                }
            } else {
                mermaidDefinition += `${question.id}(${question.title});\n`;
            }

            clicks.push(`click ${question.id} callback\n`);
        });

        mermaidDefinition += clicks.join('\n');
        mermaidDefinition += 'classDef base fill:#fff,stroke:#007bff,stroke-width:1px\n';
        mermaidDefinition += `class ${questionList.join(',')} base\n`;
    } else {
        throw new Error('Unable to render question map');
    }

    return mermaidDefinition;
};

export {
    getNextQuestionId,
    generateQuestionSchema,
    extractQuestionAnswer,
    createMermaidDefinition,
    createFormData,
};
